(() => {
  const hubs = document.querySelectorAll("[data-portfolio-hub]");
  const responsiveWidths = [480, 720, 960, 1280, 1600, 2000];
  const imageSizes = "(max-width: 599px) calc(100vw - 2rem), (max-width: 1500px) calc((100vw - 3rem) / 2), 704px";
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const fineHover = window.matchMedia("(hover: hover) and (pointer: fine)");
  const saveData = Boolean(navigator.connection?.saveData);

  const sourceAtWidth = (source, width) => {
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set("width", String(width));
      return url.toString();
    } catch (error) {
      return source;
    }
  };

  const responsiveSourceSet = (source) => responsiveWidths
    .map((width) => `${sourceAtWidth(source, width)} ${width}w`)
    .join(", ");

  hubs.forEach((hub) => {
    if (hub.dataset.portfolioHubInitialized === "true") return;
    hub.dataset.portfolioHubInitialized = "true";

    const buttons = [...hub.querySelectorAll("[data-portfolio-filter]")];
    const sourceCards = [...hub.querySelectorAll(
      "[data-portfolio-card][data-portfolio-groups], [data-portfolio-card][data-portfolio-group]"
    )];
    const grid = hub.querySelector(".portfolio-hub__grid");
    const metaobjectPayload = hub.querySelector("[data-portfolio-metaobjects]");
    let portfolioEntries = [];

    try {
      portfolioEntries = JSON.parse(metaobjectPayload?.textContent || "[]");
    } catch (error) {
      portfolioEntries = [];
    }

    const entriesByHandle = new Map(
      portfolioEntries.map((entry) => [String(entry.handle || ""), entry])
    );

    const cards = sourceCards.filter((card) => {
      const entry = entriesByHandle.get(card.dataset.portfolioHandle || "");
      if (!entry) return true;

      if (entry.show !== true) {
        card.remove();
        return false;
      }

      card.dataset.portfolioOrder = String(entry.order ?? 999999);
      return true;
    });

    const readOrder = (card) => {
      const order = Number(card.dataset.portfolioOrder);
      return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER;
    };

    cards
      .sort((a, b) => {
        const orderDifference = readOrder(a) - readOrder(b);
        if (orderDifference) return orderDifference;

        return Number(a.dataset.portfolioSourceIndex) - Number(b.dataset.portfolioSourceIndex);
      })
      .forEach((card) => grid?.appendChild(card));

    const sliderStates = new Map();
    const activePointers = new Map();

    const markImageLoaded = (image) => {
      image.classList.add("is-loaded");
    };

    const hydrateImage = (state, index, priority = "low") => {
      if (index < 1 || index >= state.count) return;

      const image = state.track.querySelector(
        `[data-portfolio-slide-index="${index}"] [data-portfolio-deferred-image]`
      );
      if (!image) return;

      if (image.dataset.hydrated === "true") {
        if (priority === "high") {
          image.loading = "eager";
          image.fetchPriority = "high";
        }
        return;
      }

      const source = image.dataset.src;
      if (!source) return;

      image.dataset.hydrated = "true";
      image.sizes = imageSizes;
      image.loading = priority === "high" ? "eager" : "lazy";
      image.fetchPriority = priority;
      image.srcset = responsiveSourceSet(source);
      image.src = sourceAtWidth(source, priority === "high" ? 1600 : 960);

      image.addEventListener("load", () => markImageLoaded(image), { once: true });
      image.addEventListener("error", () => markImageLoaded(image), { once: true });

      if (image.complete) markImageLoaded(image);
    };

    const prepareSlider = (state) => {
      if (state.prepared) return;

      const fragment = document.createDocumentFragment();

      state.gallery.forEach((item, galleryIndex) => {
        const index = galleryIndex + 1;
        const slide = document.createElement("a");
        const figure = document.createElement("figure");
        const image = document.createElement("img");

        slide.className = "portfolio-card__slide portfolio-card__link";
        slide.href = state.caseStudyUrl;
        slide.dataset.portfolioSlideLink = "";
        slide.dataset.portfolioSlideIndex = String(index);
        slide.setAttribute("aria-label", `${state.title}, image ${index + 1} of ${state.count}`);
        slide.setAttribute("aria-hidden", "true");
        slide.tabIndex = -1;

        figure.className = "portfolio-card__media";
        image.dataset.portfolioDeferredImage = "";
        image.dataset.yntResponsiveImage = "";
        image.dataset.src = String(item.src || "");
        image.width = Number(item.width) || 1600;
        image.height = Number(item.height) || 1200;
        image.alt = String(item.alt || `${state.title} packaging design image ${index + 1}`);
        image.loading = "lazy";
        image.decoding = "async";
        image.draggable = false;

        figure.appendChild(image);
        slide.appendChild(figure);
        fragment.appendChild(slide);
      });

      state.track.appendChild(fragment);
      state.prepared = true;
    };

    const updateActiveSlide = (state, requestedIndex, hydrateNeighbours = false) => {
      const index = Math.max(0, Math.min(requestedIndex, state.count - 1));
      if (state.active !== index) {
        state.active = index;

        state.track.querySelectorAll("[data-portfolio-slide-index]").forEach((slide) => {
          const isActive = Number(slide.dataset.portfolioSlideIndex) === index;
          slide.classList.toggle("is-active", isActive);
          slide.setAttribute("aria-hidden", String(!isActive));
          slide.tabIndex = isActive ? 0 : -1;
        });

        state.previousButton.disabled = index === 0;
        state.nextButton.disabled = index === state.count - 1;
        state.counterCurrent.textContent = String(index + 1);
        state.counter.setAttribute("aria-label", `Image ${index + 1} of ${state.count}`);
      }

      hydrateImage(state, index, "high");

      if (hydrateNeighbours && !saveData) {
        hydrateImage(state, index + 1, "low");
        hydrateImage(state, index - 1, "low");
      }
    };

    const finishProgrammaticScroll = (state) => {
      window.clearTimeout(state.scrollEndTimer);
      state.scrollEndTimer = 0;
      if (state.programmaticTarget === null) return;

      const target = state.programmaticTarget;
      state.programmaticTarget = null;
      updateActiveSlide(state, target, true);
    };

    const scheduleProgrammaticFinish = (state, delay = 120) => {
      window.clearTimeout(state.scrollEndTimer);
      state.scrollEndTimer = window.setTimeout(() => finishProgrammaticScroll(state), delay);
    };

    const goToSlide = (state, requestedIndex, options = {}) => {
      prepareSlider(state);
      const index = Math.max(0, Math.min(requestedIndex, state.count - 1));
      hydrateImage(state, index, "high");
      updateActiveSlide(state, index, true);

      const targetLeft = index * state.track.clientWidth;
      if (Math.abs(state.track.scrollLeft - targetLeft) > 1) {
        state.programmaticTarget = index;
        state.track.scrollTo({
          left: targetLeft,
          behavior: reducedMotion.matches ? "auto" : "smooth",
        });
        scheduleProgrammaticFinish(state, reducedMotion.matches ? 40 : 650);
      } else {
        state.programmaticTarget = null;
        window.clearTimeout(state.scrollEndTimer);
        state.scrollEndTimer = 0;
      }

      if (options.focusSlide) {
        state.track.querySelector(`[data-portfolio-slide-index="${index}"]`)?.focus({
          preventScroll: true,
        });
      }

      if (options.control?.disabled && document.activeElement === options.control) {
        const alternateControl = options.control === state.nextButton
          ? state.previousButton
          : state.nextButton;
        alternateControl.focus({ preventScroll: true });
      }
    };

    const createSlider = (card, entry) => {
      const gallery = Array.isArray(entry?.gallery)
        ? entry.gallery.filter((item) => item && item.src)
        : [];
      if (!gallery.length) return;

      const frame = card.querySelector("[data-portfolio-slider-frame]");
      const track = card.querySelector("[data-portfolio-slider-track]");
      const mainLink = track?.querySelector("[data-portfolio-slide-index=\"0\"]");
      const titleElement = frame?.querySelector("h3");
      if (!frame || !track || !mainLink || !titleElement) return;

      const title = titleElement.textContent.trim() || "Portfolio project";
      const count = gallery.length + 1;
      const trackId = `${titleElement.id}-Track`;
      const previousButton = document.createElement("button");
      const nextButton = document.createElement("button");
      const counter = document.createElement("p");
      const counterCurrent = document.createElement("span");
      const counterSeparator = document.createTextNode(" / ");
      const counterTotal = document.createElement("span");

      track.id = trackId;
      frame.classList.add("is-slider");
      frame.setAttribute("role", "region");
      frame.setAttribute("aria-roledescription", "carousel");
      frame.setAttribute("aria-labelledby", titleElement.id);

      mainLink.setAttribute("aria-label", `View ${title} case study, image 1 of ${count}`);
      mainLink.setAttribute("aria-hidden", "false");

      previousButton.type = "button";
      previousButton.className = "portfolio-card__slider-button portfolio-card__slider-button--previous";
      previousButton.dataset.portfolioSliderDirection = "-1";
      previousButton.setAttribute("aria-controls", trackId);
      previousButton.setAttribute("aria-label", `Previous ${title} image`);
      previousButton.textContent = "‹";
      previousButton.disabled = true;

      nextButton.type = "button";
      nextButton.className = "portfolio-card__slider-button portfolio-card__slider-button--next";
      nextButton.dataset.portfolioSliderDirection = "1";
      nextButton.setAttribute("aria-controls", trackId);
      nextButton.setAttribute("aria-label", `Next ${title} image`);
      nextButton.textContent = "›";

      counter.className = "portfolio-card__slider-counter";
      counter.dataset.portfolioSliderCounter = "";
      counter.setAttribute("role", "status");
      counter.setAttribute("aria-live", "polite");
      counter.setAttribute("aria-atomic", "true");
      counter.setAttribute("aria-label", `Image 1 of ${count}`);
      counterCurrent.textContent = "1";
      counterTotal.textContent = String(count);
      counter.append(counterCurrent, counterSeparator, counterTotal);

      frame.append(previousButton, nextButton, counter);

      const state = {
        active: 0,
        card,
        caseStudyUrl: mainLink.href,
        count,
        counter,
        counterCurrent,
        dragSnapTimer: 0,
        frame,
        gallery,
        hoverTimer: 0,
        nextButton,
        pointer: null,
        prepared: false,
        previousButton,
        programmaticTarget: null,
        scrollFrame: 0,
        scrollEndTimer: 0,
        suppressClickUntil: 0,
        title,
        track,
      };

      sliderStates.set(frame, state);
    };

    cards.forEach((card) => {
      createSlider(card, entriesByHandle.get(card.dataset.portfolioHandle || ""));
    });

    const nearbyObserver = "IntersectionObserver" in window
      ? new IntersectionObserver((observations, observer) => {
        observations.forEach((observation) => {
          if (!observation.isIntersecting) return;
          const state = sliderStates.get(observation.target);
          if (state) prepareSlider(state);
          observer.unobserve(observation.target);
        });
      }, { rootMargin: "350px 0px" })
      : null;

    sliderStates.forEach((state) => {
      if (nearbyObserver) {
        nearbyObserver.observe(state.frame);
      }
    });

    hub.addEventListener("click", (event) => {
      const control = event.target.closest("[data-portfolio-slider-direction]");
      if (control) {
        const state = sliderStates.get(control.closest("[data-portfolio-slider-frame]"));
        if (!state) return;
        event.preventDefault();
        event.stopPropagation();
        goToSlide(state, state.active + Number(control.dataset.portfolioSliderDirection), {
          control,
        });
        return;
      }

      const slideLink = event.target.closest("[data-portfolio-slide-link]");
      const state = sliderStates.get(slideLink?.closest("[data-portfolio-slider-frame]"));
      if (state && Date.now() < state.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    hub.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      const state = sliderStates.get(event.target.closest("[data-portfolio-slider-frame]"));
      if (!state) return;

      let index = state.active;
      if (event.key === "ArrowLeft") index -= 1;
      if (event.key === "ArrowRight") index += 1;
      if (event.key === "Home") index = 0;
      if (event.key === "End") index = state.count - 1;

      event.preventDefault();
      goToSlide(state, index, {
        control: event.target.closest("[data-portfolio-slider-direction]"),
        focusSlide: Boolean(event.target.closest("[data-portfolio-slide-link]")),
      });
    });

    hub.addEventListener("focusin", (event) => {
      const state = sliderStates.get(event.target.closest("[data-portfolio-slider-frame]"));
      if (!state) return;
      prepareSlider(state);
      if (!saveData) hydrateImage(state, Math.min(state.active + 1, state.count - 1), "low");
    });

    hub.addEventListener("pointerover", (event) => {
      if (!fineHover.matches) return;
      const frame = event.target.closest("[data-portfolio-slider-frame]");
      const state = sliderStates.get(frame);
      if (!state || frame.contains(event.relatedTarget)) return;

      window.clearTimeout(state.hoverTimer);
      state.hoverTimer = window.setTimeout(() => {
        prepareSlider(state);
        if (!saveData) hydrateImage(state, Math.min(state.active + 1, state.count - 1), "low");
      }, 180);
    });

    hub.addEventListener("pointerout", (event) => {
      const frame = event.target.closest("[data-portfolio-slider-frame]");
      const state = sliderStates.get(frame);
      if (!state || frame.contains(event.relatedTarget)) return;
      window.clearTimeout(state.hoverTimer);
    });

    hub.addEventListener("pointerdown", (event) => {
      const track = event.target.closest("[data-portfolio-slider-track]");
      const state = sliderStates.get(track?.closest("[data-portfolio-slider-frame]"));
      if (!state || event.button > 0 || state.pointer) return;

      prepareSlider(state);
      window.clearTimeout(state.dragSnapTimer);
      state.dragSnapTimer = 0;
      if (state.programmaticTarget !== null) {
        const currentLeft = state.track.scrollLeft;
        state.track.style.scrollBehavior = "auto";
        state.track.scrollLeft = currentLeft;
        state.track.style.removeProperty("scroll-behavior");
        state.programmaticTarget = null;
        window.clearTimeout(state.scrollEndTimer);
        state.scrollEndTimer = 0;
      }
      state.pointer = {
        dragged: false,
        id: event.pointerId,
        pointerType: event.pointerType,
        startScroll: state.track.scrollLeft,
        startIndex: state.active,
        targetIndex: state.active,
        x: event.clientX,
        y: event.clientY,
      };
      activePointers.set(event.pointerId, state);
    });

    hub.addEventListener("pointermove", (event) => {
      const state = activePointers.get(event.pointerId);
      if (!state || state.pointer?.id !== event.pointerId) return;

      const deltaX = event.clientX - state.pointer.x;
      const deltaY = event.clientY - state.pointer.y;
      const horizontalMove = Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY);
      if (!horizontalMove) return;

      state.pointer.dragged = true;
      state.suppressClickUntil = Date.now() + 500;
      if (Math.abs(deltaX) >= 40) {
        state.pointer.targetIndex = Math.max(
          0,
          Math.min(
            state.pointer.startIndex + (deltaX < 0 ? 1 : -1),
            state.count - 1,
          ),
        );
      }
      hydrateImage(
        state,
        state.pointer.targetIndex,
        "high"
      );

      if (state.pointer.pointerType === "mouse") {
        try {
          if (!state.track.hasPointerCapture(event.pointerId)) {
            state.track.setPointerCapture(event.pointerId);
          }
        } catch (error) {
          // Pointer capture is an enhancement; window-level release still clears state.
        }

        event.preventDefault();
        state.track.classList.add("is-dragging");
        state.track.scrollLeft = state.pointer.startScroll - deltaX;
      }
    });

    const finishPointer = (event) => {
      const state = activePointers.get(event.pointerId);
      if (!state || state.pointer?.id !== event.pointerId) return;

      const wasDragged = state.pointer.dragged;
      const pointerType = state.pointer.pointerType;
      const targetIndex = state.pointer.targetIndex;
      activePointers.delete(event.pointerId);

      try {
        if (state.track.hasPointerCapture(event.pointerId)) {
          state.track.releasePointerCapture(event.pointerId);
        }
      } catch (error) {
        // The pointer may already have been released by the browser.
      }

      state.pointer = null;
      state.track.classList.remove("is-dragging");

      if (wasDragged) {
        state.suppressClickUntil = Date.now() + 500;
        const index = Number.isInteger(targetIndex)
          ? targetIndex
          : Math.round(state.track.scrollLeft / Math.max(state.track.clientWidth, 1));

        if (pointerType === "mouse") {
          state.dragSnapTimer = window.setTimeout(() => {
            state.dragSnapTimer = 0;
            hydrateImage(state, index, "high");
            updateActiveSlide(state, index, true);
            state.track.style.scrollBehavior = "auto";
            state.track.scrollLeft = index * state.track.clientWidth;
            state.track.style.removeProperty("scroll-behavior");
          }, 50);
        } else {
          window.requestAnimationFrame(() => goToSlide(state, index));
        }
      }
    };

    window.addEventListener("pointerup", finishPointer);
    window.addEventListener("pointercancel", finishPointer);
    hub.addEventListener("dragstart", (event) => {
      if (event.target.closest("[data-portfolio-slider-track]")) event.preventDefault();
    });

    hub.addEventListener("scroll", (event) => {
      const track = event.target.closest?.("[data-portfolio-slider-track]");
      const state = sliderStates.get(track?.closest("[data-portfolio-slider-frame]"));
      if (!state || !state.prepared || state.scrollFrame) return;

      state.scrollFrame = window.requestAnimationFrame(() => {
        state.scrollFrame = 0;

        if (state.programmaticTarget !== null) {
          scheduleProgrammaticFinish(state);
          return;
        }

        const index = Math.round(state.track.scrollLeft / Math.max(state.track.clientWidth, 1));
        if (index !== state.active) updateActiveSlide(state, index, true);
      });
    }, true);

    let resizeFrame = 0;
    window.addEventListener("resize", () => {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0;
        sliderStates.forEach((state) => {
          if (state.prepared) state.track.scrollLeft = state.active * state.track.clientWidth;
        });
      });
    }, { passive: true });

    const applyFilter = (filter) => {
      buttons.forEach((button) => {
        const active = button.dataset.portfolioFilter === filter;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });

      cards.forEach((card) => {
        const groups = (card.dataset.portfolioGroups || card.dataset.portfolioGroup || "")
          .split(/\s+/)
          .filter(Boolean);
        const visible = filter === "all" || groups.includes(filter);
        card.hidden = !visible;
      });
    };

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        applyFilter(button.dataset.portfolioFilter || "all");
      });
    });

    const requestedFilter = new URLSearchParams(window.location.search).get("category");
    const hasRequestedFilter = buttons.some(
      (button) => button.dataset.portfolioFilter === requestedFilter,
    );
    applyFilter(hasRequestedFilter ? requestedFilter : "all");
  });
})();
